// src/pages/admin/AdminReports.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import { downloadBlob } from "../../utils/download";

const numberFormatter = new Intl.NumberFormat("en-NG", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatAmount = (value) => numberFormatter.format(Number(value || 0));

const AdminReports = () => {
  const [filters, setFilters] = useState({ start: "", end: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchReport = useCallback(
    async (overrideFilters) => {
      setLoading(true);
      setError("");
      try {
        const params = {};
        const effective = { ...filtersRef.current, ...(overrideFilters || {}) };
        if (effective.start) params.start = effective.start;
        if (effective.end) params.end = effective.end;
        const data = await api.adminFetchFinanceReport(params);
        setReport(data);
      } catch (err) {
        setError(err.message || "Unable to load finance report");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const balanceSheet = report?.balance_sheet;
  const pnl = report?.profit_and_loss;
  const trialBalance = useMemo(() => report?.trial_balance || [], [report]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Finance Reports</h1>
          <p className="mt-1 text-sm text-slate-500">
            IFRS-style view of ledger balances. Use the filters to slice by posting date
            and export the trial balance for further analysis.
          </p>
        </div>
        <form
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm md:flex-row md:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            fetchReport();
          }}
        >
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, start: event.target.value }))
              }
              className="w-full rounded border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
              End Date
            </label>
            <input
              type="date"
              value={filters.end}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, end: event.target.value }))
              }
              className="w-full rounded border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
          </div>
          <button
            type="submit"
            className="rounded bg-armyGreen px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  setExporting(true);
                  const blob = await api.adminExportFinanceReport({ section: "trial", format: "xlsx", ...filtersRef.current });
                  const stamp = new Date().toISOString().slice(0, 10);
                  downloadBlob(blob, `trial-balance-${stamp}.xlsx`);
                } catch (err) {
                  alert(err.message || "Unable to export trial balance.");
                } finally {
                  setExporting(false);
                }
              }}
              className="rounded border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
              disabled={exporting || loading}
            >
              {exporting ? "Exporting..." : "Export Trial Balance (Excel)"}
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  setExporting(true);
                  const blob = await api.adminExportFinanceReport({ section: "full", format: "xlsx", ...filtersRef.current });
                  const stamp = new Date().toISOString().slice(0, 10);
                  downloadBlob(blob, `finance-reports-${stamp}.xlsx`);
                } catch (err) {
                  alert(err.message || "Unable to export finance report.");
                } finally {
                  setExporting(false);
                }
              }}
              className="rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              disabled={exporting || loading}
            >
              {exporting ? "Exporting..." : "Export Full (Excel)"}
            </button>
          </div>
        </form>
      </header>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Balance Sheet Snapshot
            </h2>
            <p className="text-xs text-slate-400">
              Assets vs liabilities &amp; equity based on posted ledger entries. P&amp;L nets into equity for balance purposes.
            </p>
          </div>
          <div className="space-y-3 px-4 py-5 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span className="font-medium">Assets</span>
              <span>{formatAmount(balanceSheet?.assets)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium">Liabilities</span>
              <span>{formatAmount(balanceSheet?.liabilities)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium">Equity (base)</span>
              <span>{formatAmount(balanceSheet?.equity_base ?? balanceSheet?.equity)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium">P&amp;L adjustment</span>
              <span>{formatAmount(balanceSheet?.pnl_adjustment)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium">Equity (incl. P&amp;L)</span>
              <span>{formatAmount(balanceSheet?.equity)}</span>
            </div>
            <div
              className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${
                (balanceSheet?.difference || 0) === 0
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              <span className="font-semibold">Check</span>
              <span>{formatAmount(balanceSheet?.difference)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Profit &amp; Loss
            </h2>
            <p className="text-xs text-slate-400">
              Income and expense classes from the ledger for the selected period.
            </p>
          </div>
          <div className="space-y-3 px-4 py-5 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span className="font-medium">Income</span>
              <span>{formatAmount(pnl?.income)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium">Expenses</span>
              <span>{formatAmount(pnl?.expenses)}</span>
            </div>
            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 font-semibold">
              <span>Net Income</span>
              <span>{formatAmount(pnl?.net_income)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Trial Balance
          </h2>
          <p className="text-xs text-slate-400">
            Debits and credits by account code. Export to CSV from your browser if
            required.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3 text-right">Debit</th>
                <th className="px-4 py-3 text-right">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {loading && !trialBalance.length ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-slate-500">
                    Loading ledger balances&hellip;
                  </td>
                </tr>
              ) : trialBalance.length ? (
                trialBalance.map((row) => (
                  <tr key={row.account.code}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{row.account.code}</div>
                      <div className="text-xs text-slate-500">{row.account.name}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatAmount(row.debit)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatAmount(row.credit)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-slate-500">
                    No ledger activity found for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminReports;
